import { useState, useEffect, useCallback } from 'react';

const DB_NAME = 'aiviora-offline';
const STORE_NAME = 'pending-sales';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function useOfflineQueue() {
  const [pendingCount, setPendingCount] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const refreshCount = useCallback(async () => {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const count = await new Promise((res, rej) => {
        const req = tx.objectStore(STORE_NAME).count();
        req.onsuccess = () => res(req.result);
        req.onerror = () => rej(req.error);
      });
      setPendingCount(count);
    } catch {
      setPendingCount(0);
    }
  }, []);

  const enqueue = useCallback(async (saleData) => {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await new Promise((res, rej) => {
      const req = tx.objectStore(STORE_NAME).add({
        ...saleData,
        queued_at: new Date().toISOString(),
      });
      req.onsuccess = () => res(req.result);
      req.onerror = () => rej(req.error);
    });
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      const reg = await navigator.serviceWorker.ready;
      await reg.sync.register('sync-offline-sales').catch(() => {});
    }
    await refreshCount();
  }, [refreshCount]);

  const clearAll = useCallback(async () => {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await new Promise((res, rej) => {
      const req = tx.objectStore(STORE_NAME).clear();
      req.onsuccess = () => res();
      req.onerror = () => rej(req.error);
    });
    await refreshCount();
  }, [refreshCount]);

  useEffect(() => {
    refreshCount();
    const onOnline = () => { setIsOnline(true); refreshCount(); };
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [refreshCount]);

  return { pendingCount, isOnline, enqueue, clearAll, refreshCount };
}
